import { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "./Modal";
import Pagination from "./Pagination";
import "jspdf-autotable";
import { useSelector } from "react-redux";
import {
  getOrganisation,
  getUser,
  payoutData,
  payoutDataUSDTReport,
} from "../../../config/LiveApi";
import "./Report.css";
import Spinner from "../../Spin/Spinner";
import DataTable from "../../../components/common/DataTable/DataTable";
import CopyCell from "../../../components/common/TableCells/CopyCell";
import StatusCell from "../../../components/common/TableCells/StatusCell";
import DateCell from "../../../components/common/TableCells/DateCell";

function ReportTable({
  currentItems,
  setCurrentItems,
  tableData,
  page,
  setPage,
  totalPages,
  startDate,
  endDate,
  isSearching,
  reportType,
}) {
  const [modalData, setModalData] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [orgOptions, setOrgOptions] = useState([]);
  const [selectedOrgName, setSelectedOrgName] = useState("All");
  const [listofusers, setListofUser] = useState([]);
  const [selectedBusinessName, setSelectedBusinessName] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedMode, setSelectedMode] = useState("All");
  const [loading, setLoading] = useState(false);

  const businessOptions = useMemo(() => {
    if (!Array.isArray(listofusers)) return [];

    return listofusers.map((user) => ({
      value: user.id,
      label: user?.alias || user?.id,
    }));
  }, [listofusers]);

  const orgSelectOptions = useMemo(() => {
    if (!Array.isArray(orgOptions)) return [];

    return orgOptions.map((org) => ({
      value: org.orgName,
      label: org.orgName,
    }));
  }, [orgOptions]);

  const role = useSelector((store) => store.userReducer.role);
  const token = useSelector((store) => store.userReducer.token);
  const viewData = (item, setModalData, setOpenModal) => {
    setModalData(item);
    setOpenModal(true);
  };
  const dataToShow = isSearching ? tableData : currentItems;

  useEffect(() => {
    if (isSearching) return;
    const fetchOrganisations = async () => {
      try {
        if (role === "mops" || role === "admin" || role === "subAdmin") return;
        const response = await getOrganisation(token);
        const orgData = response.data;
        setOrgOptions(orgData);
      } catch (err) {
        console.error("Error fetching organisations:", err.message);
      }
    };

    fetchOrganisations();
  }, [token, isSearching, role]);

  const fetchUsers = useCallback(async () => {
    try {
      if (role === "mops" || role === "admin" || role === "subAdmin") return;
      const response = await getUser(token);

      const data = response?.data;
      const adminUsers = data.filter((user) => user.role === "admin");

      setListofUser(adminUsers);
    } catch (error) {
      console.log(error.message);
    }
  }, [token, role]);

  useEffect(() => {
    if (isSearching) return;
    fetchUsers();
  }, [isSearching, fetchUsers]);

  useEffect(() => {
    if (isSearching) return;
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const response =
          reportType === "usdt"
            ? await payoutDataUSDTReport(
                token,
                page,
                startDate,
                endDate,
                selectedBusinessName === "All" ? "" : selectedBusinessName,
                selectedStatus === "All" ? "" : selectedStatus,
              )
            : await payoutData(
                token,
                page,
                startDate,
                endDate,
                selectedOrgName === "All" ? "" : selectedOrgName,
                selectedBusinessName === "All" ? "" : selectedBusinessName,
                selectedStatus === "All" ? "" : selectedStatus,
                selectedMode === "All" ? "" : selectedMode,
              );

        setCurrentItems(response);
      } catch (error) {
        console.error("Error fetching transactions:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [
    selectedOrgName,
    selectedBusinessName,
    selectedStatus,
    selectedMode,
    isSearching,
    startDate,
    endDate,
    page,
    setCurrentItems,
    token,
    reportType,
  ]);

  const listofMode = ["IMPS", "UPI"];

  const columns = [];

  if (
    role === "super" ||
    role === "operation" ||
    role === "trafficManager" ||
    role === "subSuper"
  ) {
    if (reportType !== "usdt") {
      columns.push({
        key: "orgName",
        header: "Org Name",
        filterable: true,
        filterType: "select",
        filterOptions: [{ value: "All", label: "All" }, ...orgSelectOptions],
        filterValue: selectedOrgName,
        onFilterChange: setSelectedOrgName,
        render: (row) => row.orgName || "N/A",
      });
    }
    columns.push(
      {
        key: "businessName",
        header: "Business Name",
        filterable: true,
        filterType: "select",
        filterOptions: [{ value: "All", label: "All" }, ...businessOptions],
        filterValue: selectedBusinessName,
        onFilterChange: setSelectedBusinessName,
        render: (row) => row.alias || row.userId,
      },
      {
        key: "pg",
        header: "PG Name",
        render: (row) => row.pg || "",
      },
    );
  }

  columns.push(
    {
      key: "orderId",
      header: "Order Id",
      wrapCell: true,
      render: (row) => <CopyCell value={row.orderId} />,
    },

    {
      key: "mode",
      header: "Transfer Mode",
      filterable: true,
      filterType: "menu",
      filterOptions: [
        { value: "All", label: "All" },
        { value: "IMPS", label: "IMPS" },
        { value: "UPI", label: "UPI" },
      ],
      filterValue: selectedMode,
      onFilterChange: setSelectedMode,
      render: (row) => row?.requestData?.mode || "N/A",
    },

    {
      key: "amount",
      header: "Amount (₹)",
      render: (row) => row?.requestData?.amount,
    },

    {
      key: "commission",
      header: "Comm.(₹)",
      render: (row) =>
        row.responseStatus === "SETTLED" ? row.totalComission : 0,
    },

    {
      key: "createdAt",
      header: "Transaction Date",
      render: (row) => <DateCell date={row.createdAt} />,
    },

    {
      key: "updatedAt",
      header: "Updated Date",
      render: (row) => <DateCell date={row.updatedAt} />,
    },

    {
      key: "status",
      header: "Status",
      filterable: true,
      filterType: "menu",
      thProps: {
        "data-tour": "payout-status",
      },
      filterOptions: [
        { value: "All", label: "All" },
        { value: "SETTLED", label: "Settled" },
        {
          value: "SETTLEMENT_PROCESS",
          label: "Processing",
        },
        {
          value: "SETTLEMENT_FAILED",
          label: "Failed",
        },
      ],
      filterValue: selectedStatus,
      onFilterChange: setSelectedStatus,
      render: (row) => (
        <StatusCell
          status={
            row.responseStatus === "SETTLEMENT_PROCESS"
              ? "PROCESSING"
              : row.responseStatus === "SETTLEMENT_FAILED"
                ? "FAILED"
                : row.responseStatus
          }
        />
      ),
    },

    {
      key: "action",
      header: "Action",
      thProps: {
        "data-tour": "payout-transaction-details",
      },
      render: (row) => (
        <button
          className="view-btn"
          onClick={() => {
            setModalData(row);
            setOpenModal(true);
          }}
        >
          View
        </button>
      ),
    },
  );

  const formattedData = useMemo(() => {
    return (dataToShow || []).map((item) => ({
      ...item,
      requestData: JSON.parse(item.request || "{}"),
      responseData: JSON.parse(item.resposne || "{}"),
    }));
  }, [dataToShow]);

  return (
    <>
      <div data-tour="payout-transfer-details" className="table-main-report">
        {loading ? (
          <Spinner />
        ) : (
          <DataTable columns={columns} data={formattedData} loading={loading} />
        )}

        <div>
          <Pagination page={page} setPage={setPage} totalPages={totalPages} />
        </div>
      </div>

      {openModal && modalData && (
        <Modal modalData={modalData} setOpenModal={setOpenModal} />
      )}
    </>
  );
}

export default ReportTable;